<script>
// AiDraftBanner — compact strip above the reply box showing the LATEST AI
// draft for this conversation (CS-automation 1.2, 2026-06-10).
//
// Owner direction (gate 1.1): the feed was flooded with AI cards — show only
// the newest draft, small, like a status banner; history stays in the feed
// as collapsed notes (see bubbles/Text/Index.vue). Send / Edit / Dismiss live
// here; dismiss reasons feed mk-backend's /api/ai-improvement (the /ai-improve
// weekly loop reads them).
import { mapGetters } from 'vuex';
import MemoryPopover from 'next/message/MemoryPopover.vue';

const DISMISS_REASONS = [
  { key: 'wrong-stock', label: 'Wrong stock/price' },
  { key: 'wrong-language', label: 'Wrong language' },
  { key: 'didnt-answer', label: "Didn't answer the question" },
  { key: 'other', label: 'Other' },
];

export default {
  name: 'AiDraftBanner',
  components: { MemoryPopover },
  data() {
    return {
      sendState: 'idle',
      showDismiss: false,
      showComment: false,
      commentState: 'idle',
      expanded: false,
      // message ids hidden after send/dismiss — persisted so a dismissed
      // draft stays dismissed across reloads (localStorage, capped at 300)
      hiddenIds: JSON.parse(localStorage.getItem('mhAiDraftHidden') || '[]'),
      dismissReasons: DISMISS_REASONS,
      cardData: {},
      // SKUs the agent unticked — excluded from the send (old-card behavior)
      excludedSkus: [],
      // CS-automation 2.6 countdown: ticking clock + veto state for drafts
      // carrying content_attributes.pending_send (daytime autopilot veto
      // window). 'vetoed' reverts the banner to a normal draft; 'sentNow'
      // hides it (explicit human approval fired the send).
      nowMs: Date.now(),
      cdTimer: null,
      cdState: 'idle',
    };
  },
  mounted() {
    this.cdTimer = setInterval(() => {
      if (this.attrs && this.attrs.pending_send) this.nowMs = Date.now();
    }, 1000);
  },
  beforeUnmount() {
    if (this.cdTimer) clearInterval(this.cdTimer);
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
    }),
    accountId() {
      const m = window.location.pathname.match(/accounts\/(\d+)/);
      return m ? m[1] : '';
    },
    messages() {
      return this.currentChat?.messages || [];
    },
    latestDraft() {
      const msgs = this.messages;
      let lastOutgoingTs = 0;
      msgs.forEach(m => {
        if (m.message_type === 1 && !m.private) {
          lastOutgoingTs = Math.max(lastOutgoingTs, m.created_at || 0);
        }
      });
      for (let i = msgs.length - 1; i >= 0; i -= 1) {
        const m = msgs[i];
        if (!m.private) continue; // drafts are private notes
        const c = m.content || '';
        if (!c.startsWith('💡 AI Draft')) continue;
        if (this.hiddenIds.includes(m.id)) return null;
        // a human (or auto) reply landed after this draft → it's history now
        if ((m.created_at || 0) <= lastOutgoingTs) return null;
        return m;
      }
      return null;
    },
    attrs() {
      return this.latestDraft?.content_attributes || {};
    },
    backendUrl() {
      return (
        this.attrs.backend_url ||
        this.attrs.backendUrl ||
        'https://sapi.mkhb.my'
      );
    },
    draftHeader() {
      const m = (this.latestDraft?.content || '').match(
        /^(💡 AI Draft \(([^)]+)\))/
      );
      return m ? m[2] : 'AI draft';
    },
    draftFullText() {
      const m = (this.latestDraft?.content || '').match(
        /^💡 AI Draft \([^)]+\)\n\n([\s\S]+)$/
      );
      return m ? m[1].trim() : '';
    },
    draftDisplayText() {
      return this.draftFullText
        .replace(/\[Product:[^\]]+\]/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    },
    productCount() {
      return (this.draftFullText.match(/\[Product:[^\]]+\]/g) || []).length;
    },
    // 2.6: pending countdown send (null when not scheduled, vetoed, or fired-now)
    pendingSend() {
      const ps = this.attrs.pending_send;
      if (!ps || !ps.fire_at_ms) return null;
      if (this.cdState === 'vetoed' || this.cdState === 'sentNow') return null;
      return ps;
    },
    counting() {
      return !!this.pendingSend && this.pendingSend.fire_at_ms > this.nowMs;
    },
    countdownExpired() {
      return !!this.pendingSend && this.pendingSend.fire_at_ms <= this.nowMs;
    },
    countdownLabel() {
      if (!this.pendingSend) return '';
      const left = Math.max(
        0,
        Math.round((this.pendingSend.fire_at_ms - this.nowMs) / 1000)
      );
      const m = Math.floor(left / 60);
      const s = String(left % 60).padStart(2, '0');
      return `${m}:${s}`;
    },
    draftCards() {
      const skus = [
        ...new Set(
          (this.draftFullText.match(/\[Product:([^\]]+)\]/g) || []).map(t =>
            t
              .replace(/^\[Product:/, '')
              .replace(/\]$/, '')
              .trim()
          )
        ),
      ];
      return skus.map(sku => ({
        sku,
        card: this.cardData[sku] || null,
        included: !this.excludedSkus.includes(sku),
      }));
    },
  },
  watch: {
    // ONE watcher only — a duplicate `latestDraft` key here silently replaced
    // the card-fetch handler (caught live 2026-06-10: banner showed bare SKU
    // chips with no thumbnail/price/stock). Resets transient UI AND fetches
    // cards for the [Product:] tokens (owner condition: the product preview
    // from the old draft card must survive in the banner).
    latestDraft: {
      immediate: true,
      handler(next, prev) {
        if (next?.id !== prev?.id) {
          this.sendState = 'idle';
          this.showDismiss = false;
          this.showComment = false;
          this.commentState = 'idle';
          this.expanded = false;
          this.excludedSkus = [];
          this.cdState = 'idle';
          this.nowMs = Date.now();
        }
        this.cardData = {};
        const skus = [
          ...new Set(
            ((next?.content || '').match(/\[Product:([^\]]+)\]/g) || []).map(
              t =>
                t
                  .replace(/^\[Product:/, '')
                  .replace(/\]$/, '')
                  .trim()
            )
          ),
        ];
        if (skus.length) this.fetchCards(skus);
      },
    },
  },
  methods: {
    toggleCard(sku) {
      this.excludedSkus = this.excludedSkus.includes(sku)
        ? this.excludedSkus.filter(x => x !== sku)
        : [...this.excludedSkus, sku];
    },
    // MakerHub: card `name` is the catalogue short_name, which for a variant row
    // can be a bare label like "UNSOLDERED" (short_name == variant). Prefer the
    // real listing name then, and surface the variant as a badge. (ASRPRO1 bug
    // 2026-06-29; mirrors bubbles/Text/Index.vue.)
    cardTitle(card) {
      if (!card) return '';
      const variant = card.variant_name;
      if (variant && card.name === variant) return card.full_name || card.name;
      return card.name || card.full_name;
    },
    cardVariant(card) {
      if (!card || !card.variant_name) return '';
      return card.variant_name === this.cardTitle(card) ? '' : card.variant_name;
    },
    // Best link to verify the listing — shopee_url, then any url, else a Shopee
    // SKU search fallback (never the dead '-' placeholder form).
    productHref(card) {
      if (!card) return '#';
      const direct = card.shopee_url || card.url;
      if (direct) return direct;
      const q = card.sku || card.name;
      return q
        ? `https://shopee.com.my/search?keyword=${encodeURIComponent(q)}`
        : '#';
    },
    hide(id) {
      this.hiddenIds = [...this.hiddenIds.slice(-299), id];
      localStorage.setItem('mhAiDraftHidden', JSON.stringify(this.hiddenIds));
    },
    async fetchCards(skus) {
      try {
        const url = `/api/v1/accounts/${this.accountId}/mh_ai_assist/product_cards?skus=${encodeURIComponent(skus.join(','))}`;
        const { data } = await window.axios.get(url);
        this.cardData = data.cards || {};
      } catch {
        // chips fall back to bare SKU text
      }
    },
    sendText() {
      // Strip excluded [Product:<sku>] tokens before sending — included SKUs
      // stay in the text; outgoing processors expand them (old-card behavior).
      let text = this.draftFullText;
      this.excludedSkus.forEach(sku => {
        const re = new RegExp(
          `\\[Product:${sku.replace(/[.*+?^$()|[\\]\\\\{}]/g, '\\$&')}\\]`,
          'g'
        );
        text = text.replace(re, '');
      });
      return text.replace(/\n{3,}/g, '\n\n').trim();
    },
    async sendDraft() {
      if (!this.draftFullText || this.sendState === 'sending') return;
      this.sendState = 'sending';
      try {
        const resp = await fetch(
          `${this.backendUrl}/api/chat-triage/send-draft`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              conversationId: this.currentChat?.id,
              accountId: this.accountId,
              messageId: this.latestDraft?.id,
              text: this.sendText(),
              shopeeItemIds:
                this.attrs.shopee_item_ids || this.attrs.shopeeItemIds || [],
              products: this.attrs.products || [],
              sentBy: {
                id: this.currentUser?.id,
                name: this.currentUser?.name,
              },
            }),
          }
        );
        if (resp.ok) {
          this.sendState = 'sent';
          const id = this.latestDraft?.id;
          setTimeout(() => this.hide(id), 1200);
        } else {
          this.sendState = 'error';
        }
      } catch {
        this.sendState = 'error';
      }
    },
    // 2.6: veto the scheduled send — the pending entry dies, the draft note
    // stays and the banner reverts to the normal Send/Edit/Dismiss actions.
    async cancelCountdown(reason = 'banner-cancel') {
      if (this.cdState === 'cancelling') return;
      this.cdState = 'cancelling';
      try {
        await fetch(`${this.backendUrl}/api/chat-triage/countdown-cancel`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversationId: this.currentChat?.id,
            reason,
          }),
        });
        this.cdState = 'vetoed';
      } catch {
        this.cdState = 'error';
      }
    },
    // 2.6: explicit human approval — fire the scheduled send immediately.
    async sendNowCountdown() {
      if (this.cdState === 'sendingNow') return;
      this.cdState = 'sendingNow';
      try {
        const resp = await fetch(
          `${this.backendUrl}/api/chat-triage/countdown-send-now`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ conversationId: this.currentChat?.id }),
          }
        );
        if (resp.ok) {
          this.cdState = 'sentNow';
          const id = this.latestDraft?.id;
          setTimeout(() => this.hide(id), 1200);
        } else {
          this.cdState = 'error';
        }
      } catch {
        this.cdState = 'error';
      }
    },
    editDraft() {
      const payload = this.draftDisplayText;
      if (!payload) return;
      // editing a counting draft implies a veto first (2.6)
      if (this.counting) this.cancelCountdown('banner-edit');
      const editor = document.querySelector('.reply-box .ProseMirror');
      if (editor) {
        editor.focus();
        editor.innerHTML = `<p>${payload.replace(/\n/g, '</p><p>')}</p>`;
        editor.dispatchEvent(new Event('input', { bubbles: true }));
      }
      // editing implies the banner served its purpose
      this.hide(this.latestDraft?.id);
    },
    // Same AI-improvement comment feature as the old in-thread card — payload
    // mirrors bubbles/Text/Index.vue submitComment so the /ai-improve weekly
    // loop sees one shape. Commenting does NOT hide the banner (it's feedback,
    // not a dismissal).
    async submitComment({ note, scope }) {
      if (!note || !note.trim()) return;
      this.commentState = 'saving';
      try {
        const body = {
          kind: 'correction',
          conversationId: this.currentChat?.id,
          messageId: this.latestDraft?.id,
          inboxId: this.currentChat?.inbox_id,
          draftHeader: `💡 AI Draft (${this.draftHeader})`,
          draftText: this.draftFullText,
          comment: note.trim(),
          timestamp: new Date().toISOString(),
        };
        if (scope && scope !== 'auto') {
          body.scope = scope === 'all' ? ['all'] : [scope];
        }
        const resp = await fetch(
          `${this.backendUrl}/api/ai-improvement/comment`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }
        );
        this.commentState = resp.ok ? 'saved' : 'error';
        if (resp.ok) {
          setTimeout(() => {
            this.showComment = false;
            this.commentState = 'idle';
          }, 1500);
        }
      } catch {
        this.commentState = 'error';
      }
    },
    async dismiss(reasonKey) {
      // snapshot BEFORE hiding — hiddenIds.push nulls the latestDraft computed
      const id = this.latestDraft?.id;
      const draftHeader = `💡 AI Draft (${this.draftHeader})`;
      const draftText = this.draftFullText;
      const inboxId = this.currentChat?.inbox_id;
      const conversationId = this.currentChat?.id;
      const backendUrl = this.backendUrl;
      this.showDismiss = false;
      this.hide(id);
      try {
        await fetch(`${backendUrl}/api/ai-improvement/comment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            conversationId,
            messageId: id,
            inboxId,
            draftHeader,
            draftText,
            comment: `[DISMISSED] reason: ${reasonKey}`,
            dismissed: true,
            dismissReason: reasonKey,
            timestamp: new Date().toISOString(),
          }),
        });
      } catch {
        // dismiss is best-effort — the banner is already hidden
      }
    },
  },
};
</script>

<template>
  <div
    v-if="latestDraft"
    data-test-id="ai-draft-banner"
    class="mx-2 mb-1 rounded-lg border px-3 py-2 text-sm"
    :class="
      counting || countdownExpired
        ? 'border-n-amber-8 bg-n-amber-2'
        : 'border-n-weak bg-n-alpha-1'
    "
  >
    <div class="flex items-start gap-2">
      <span
        class="mt-0.5 inline-flex shrink-0 select-none items-center rounded-full px-1.5 py-0 text-xxs font-semibold leading-4 text-white"
        :class="counting || countdownExpired ? 'bg-n-amber-9' : 'bg-n-iris-9'"
        >{{ $t('CHAT_LIST.AI_BADGES.AI') }}</span>
      <div class="min-w-0 flex-1">
        <span
          v-if="counting"
          class="mr-2 text-xs font-semibold text-n-amber-11"
          data-test-id="ai-draft-countdown"
          >⏱ auto-sends in {{ countdownLabel }} · {{ draftHeader }}</span>
        <span
          v-else-if="countdownExpired"
          class="mr-2 text-xs font-semibold text-n-amber-11"
          >⏱ auto-sending… · {{ draftHeader }}</span>
        <span v-else
class="mr-2 text-xs font-medium opacity-60"
          >draft · {{ draftHeader
          }}<template v-if="productCount">
            · {{ productCount }} 🛒</template></span>
        <span
          class="block whitespace-pre-wrap leading-snug"
          :class="expanded ? '' : 'line-clamp-2'"
          @click="expanded = !expanded"
          >{{ draftDisplayText }}</span>
        <div v-if="draftCards.length" class="mt-1.5 flex flex-col gap-1.5">
          <label
            v-for="c in draftCards"
            :key="c.sku"
            class="flex cursor-pointer gap-3 overflow-hidden rounded-lg border border-current/10 bg-n-alpha-1 transition-opacity"
            :class="c.included ? '' : 'opacity-40'"
            data-test-id="ai-draft-card-chip"
          >
            <input
              type="checkbox"
              :checked="c.included"
              class="sr-only"
              @change="toggleCard(c.sku)"
            />
            <img
              v-if="c.card && c.card.thumbnail"
              :src="c.card.thumbnail"
              :alt="c.card.name"
              class="h-16 w-16 flex-shrink-0 object-cover"
              loading="lazy"
            />
            <div
              v-else
              class="flex h-16 w-16 flex-shrink-0 items-center justify-center bg-n-alpha-2 text-xs text-n-slate-9"
            >
              {{ c.sku }}
            </div>
            <div class="flex min-w-0 flex-1 flex-col justify-center py-2 pr-3">
              <div class="line-clamp-2 text-xs font-semibold leading-snug">
                {{ c.card ? cardTitle(c.card) : c.sku }}
              </div>
              <div class="mt-0.5 flex items-center gap-2 text-xs">
                <span
                  v-if="c.card && c.card.price"
                  class="font-semibold text-r-ruby-11"
                  >RM{{ c.card.price }}</span>
                <span
                  v-if="c.card && cardVariant(c.card)"
                  class="rounded bg-n-alpha-2 px-1 text-[10px] text-n-slate-11"
                  >{{ cardVariant(c.card) }}</span>
                <span v-if="c.card && !c.card.in_stock"
class="text-n-slate-10"
                  >out of stock</span>
                <span v-else-if="c.card"
class="text-n-slate-10"
                  >{{ c.card.stock }} avail</span>
                <a
                  v-if="c.card"
                  :href="productHref(c.card)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="ml-auto rounded border border-current/20 px-1.5 py-0.5 text-[10px] no-underline opacity-70 hover:opacity-100"
                  title="Open the listing in a new tab to verify"
                  @click.stop
                  >🔗 Open</a>
                <span class="text-[10px] opacity-70">{{
                  c.included ? '✓ include' : 'excluded'
                }}</span>
              </div>
            </div>
          </label>
        </div>
      </div>
      <div v-if="counting" class="flex shrink-0 items-center gap-1.5">
        <button
          class="rounded-md bg-n-amber-9 px-2.5 py-1 text-xs font-medium text-white"
          data-test-id="ai-draft-send-now"
          @click="sendNowCountdown"
        >
          {{ cdState === 'sendingNow' ? 'Sending…' : 'Send now' }}
        </button>
        <button
          class="rounded-md border border-n-amber-8 px-2.5 py-1 text-xs"
          data-test-id="ai-draft-edit"
          @click="editDraft"
        >
          Edit
        </button>
        <button
          class="rounded-md border border-n-amber-8 px-2.5 py-1 text-xs font-medium"
          data-test-id="ai-draft-countdown-cancel"
          @click="cancelCountdown()"
        >
          {{ cdState === 'cancelling' ? 'Cancelling…' : 'Cancel' }}
        </button>
      </div>
      <div
        v-else-if="countdownExpired"
        class="flex shrink-0 items-center gap-1.5"
      >
        <span class="px-2 text-xs opacity-70">auto-sending…</span>
      </div>
      <div v-else class="flex shrink-0 items-center gap-1.5">
        <button
          v-if="sendState === 'idle' || sendState === 'error'"
          class="rounded-md px-2.5 py-1 text-xs font-medium text-white"
          :style="{ background: sendState === 'error' ? '#dc2626' : '#1b73e8' }"
          data-test-id="ai-draft-send"
          @click="sendDraft"
        >
          {{ sendState === 'error' ? 'Retry' : 'Send' }}
        </button>
        <span
          v-else-if="sendState === 'sending'"
          class="px-2 text-xs opacity-70"
          >Sending…</span>
        <span v-else class="px-2 text-xs" style="color: #34d399">✅ Sent</span>
        <button
          class="rounded-md border border-n-weak px-2.5 py-1 text-xs"
          data-test-id="ai-draft-edit"
          @click="editDraft"
        >
          Edit
        </button>
        <div class="relative">
          <button
            class="rounded-md border border-n-weak px-2 py-1 text-xs"
            title="Comment for AI improvement"
            data-test-id="ai-draft-comment"
            @click="showComment = !showComment"
          >
            💬
          </button>
          <MemoryPopover
            v-model="showComment"
            title="AI improvement comment"
            placeholder="What should the AI do differently? (e.g. wrong product, tone, missing info...)"
            anchor="right"
            :state="commentState"
            @submit="submitComment"
          />
        </div>
        <div class="relative">
          <button
            class="rounded-md border border-n-weak px-2 py-1 text-xs opacity-70"
            data-test-id="ai-draft-dismiss"
            @click="showDismiss = !showDismiss"
          >
            ✕
          </button>
          <div
            v-if="showDismiss"
            class="absolute right-0 top-7 z-50 w-48 rounded-lg border border-n-weak bg-n-solid-1 py-1 shadow-lg"
            data-test-id="ai-draft-dismiss-menu"
          >
            <button
              v-for="r in dismissReasons"
              :key="r.key"
              class="block w-full px-3 py-1.5 text-left text-xs hover:bg-n-alpha-1"
              @click="dismiss(r.key)"
            >
              {{ r.label }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
